import { DateTime } from "luxon";

/**
 * 获取设备的时区偏移量
 * @returns 时区偏移量，例如 "-08:00"
 */
export function getDeviceTimezoneOffset(): string {
  return DateTime.local().toFormat("ZZ");
}

export function getLuxonDate(
  date: string | number | Date | null | undefined
): DateTime | null {
  if (!date) return null;
  if (typeof date === "string") return DateTime.fromISO(date);
  if (typeof date === "number") return DateTime.fromMillis(date);
  return DateTime.fromJSDate(date);
}

/**
 * 从用户的角度显示当时的训练时间，不要使用时区转换
 * 从 ISO 8601 格式的日期时间字符串中提取时间，并将其格式化为 12 小时制（例如 "9:47 AM"）。
 * 这个函数设计的目的是直接从字符串解析时间，避免 JavaScript Date 对象的时区转换问题，
 * 确保显示的是原始 ISO 字符串中表示的时间。
 * @param {string} isoDateString - 需要格式化的 ISO 8601 日期时间字符串。
 *                                例如："2025-04-30T09:47:00.000Z" 或 "2025-05-01T14:30:15.123+08:00"
 * @returns {string} 格式化后的 12 小时制时间字符串（例如 "9:47 AM"）。如果输入格式无效，则返回 "Invalid time format"。
 */
export function displayClaimedHourMinOnly(isoDateString: string): string {
  // 直接从 ISO 字符串中解析小时和分钟
  // 这样做可以避免任何时区转换
  const timeMatch = isoDateString.match(/T(\d{2}):(\d{2}):/);

  if (!timeMatch) {
    return "Invalid time format";
  }

  let hours = parseInt(timeMatch[1], 10); // e.g.: "09" => 9
  const minutes = timeMatch[2]; // e.g.: "47" => 47

  // 确定 AM/PM
  const ampm = hours >= 12 ? "PM" : "AM";

  // 将小时转换为 12 小时制
  hours = hours % 12;
  // 将小时 '0' 转换为 '12'
  hours = hours ? hours : 12;

  // 返回格式化后的时间

  if (hours < 10) return `0${hours}:${minutes} ${ampm}`;

  return `${hours}:${minutes} ${ampm}`;
}

/**
 * 根据日期与当前时间的相对关系，以易于阅读的格式显示日期或时间。
 * - 如果日期是今天，返回 ... hr ... min ago
 * - 如果日期是今年内，返回 ... days ago
 * - 如果日期是往年，返回 ... months ago
 * @param date 需要格式化的日期。可以是 ISO 字符串、数字时间戳、Date 对象，或者 null/undefined。
 *             如果传入 null 或 undefined，函数将返回 "N/A"。
 * @returns 返回根据规则格式化后的日期或时间字符串。
 */
export function displayRelativeTime(
  date: string | number | Date | null | undefined
): string {
  const luxonDate = getLuxonDate(date);

  if (!luxonDate) return "N/A";

  const today = DateTime.now();
  const diffTime = Math.abs(today.toMillis() - luxonDate.toMillis());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

  // if less than 1 min, show "Just now":
  if (diffTime < 60 * 1000) {
    return "Just now";
  }

  if (diffDays === 0) {
    if (diffHours < 1) {
      return luxonDate.toRelative({ unit: "minutes", style: "narrow" });
    } else {
      return luxonDate.toRelative({ unit: "hours", style: "narrow" });
    }
  } else if (diffDays < 30) {
    return luxonDate.toRelativeCalendar({ unit: "days", style: "narrow" });
  } else {
    return luxonDate.toRelativeCalendar({ unit: "months", style: "narrow" });
  }
}

export function formatDateForNav(
  date: string | number | Date | null | undefined
): {
  dayName: string;
  dayNumber: string;
} {
  const luxonDate = getLuxonDate(date);

  if (!luxonDate)
    return {
      dayName: "N/A",
      dayNumber: "N/A",
    };

  // if the date is before this year, then dayName should be like May'24
  if (luxonDate.year !== DateTime.now().year) {
    return {
      dayName:
        luxonDate.toFormat("LLL").toUpperCase() +
        "'" +
        luxonDate.toFormat("yy"), // e.g. Mar'24
      dayNumber: luxonDate.toFormat("dd"), // e.g. 01
    };
  }

  return {
    dayName: luxonDate.toFormat("LLL").toUpperCase(), // e.g. Mar
    dayNumber: luxonDate.toFormat("dd"), // e.g. 01
  };
}

/**
 * 根据日期与当前时间的相对关系，以易于阅读的格式显示日期或时间。
 * - 如果日期是今天，则只显示时间（例如 "02:30 PM"）。
 * - 如果日期是今年内，但不是今天，则显示月和日（例如 "Apr 30"）。
 * - 如果日期是往年，则显示月、日和年（例如 "Mar 28, 2023"）。
 * @param date 需要格式化的日期。可以是 ISO 字符串、数字时间戳、Date 对象，或者 null/undefined。
 *             如果传入 null 或 undefined，函数将返回 "Never"。
 * @returns 返回根据规则格式化后的日期或时间字符串。
 */
export function displayLocalDateHour(
  date: string | number | Date | null | undefined
): string {
  if (!date) return "Never";

  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  // const hourOnly = dateObj.toLocaleTimeString("en-US", {
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   hour12: true,
  // });
  //   // 如果日期是今天，则只显示时间
  //   const today = new Date();
  //   if (dateObj.toDateString() === today.toDateString()) {
  //     return hourOnly;
  //   }

  //   const monthAndDay = dateObj.toLocaleDateString("en-US", {
  //     month: "short",
  //     day: "numeric",
  //   });
  //   // 如果日期是今年内，但不是今天，则显示月和日
  //   const currentYear = new Date().getFullYear();
  //   if (dateObj.getFullYear() === currentYear) {
  //     return monthAndDay + " " + hourOnly;
  //   }

  // 否则，显示月、日和年
  return dateObj.toLocaleDateString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Output example: "Sep 9, 2025"
 */
export function displayLocalDate(
  date: string | number | Date | null | undefined
): string {
  const luxonDate = getLuxonDate(date);
  if (!luxonDate) return "N/A";
  return luxonDate.toFormat("MMM d, yyyy");
}

/**
 * Formats a date to show relative time with a custom prefix
 * @param date The date to format (ISO string, timestamp, Date object, or null/undefined)
 * @param prefix The prefix to add before the relative time (e.g., "Sent", "Invited", "Created")
 * @param fallback The fallback text to show if date is invalid
 * @returns Formatted string like "Sent 2 hours ago" or fallback text
 */
export function formatRelativeTimeWithPrefix(
  date: string | number | Date | null | undefined,
  prefix: string,
  fallback: string = "Recently"
): string {
  if (!date) return fallback;

  try {
    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return fallback;
    }

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return `${prefix} just now`;
    } else if (diffMinutes < 60) {
      return `${prefix} ${diffMinutes} minute${
        diffMinutes === 1 ? "" : "s"
      } ago`;
    } else if (diffHours < 24) {
      return `${prefix} ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    } else if (diffDays < 30) {
      return `${prefix} ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    } else {
      // For older dates, show the actual date
      return `${prefix} on ${dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          dateObj.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      })}`;
    }
  } catch {
    return fallback;
  }
}

/**
 * Convert 12-hour format time to 24-hour format
 * @param time12h Time in 12-hour format (e.g., "08:12 AM", "02:30 PM")
 * @returns Time in 24-hour format (e.g., "08:12", "14:30")
 */
export function convertTo24Hour(time12h: string): string {
  const [time, modifier] = time12h.split(" ");
  const [hours, minutes] = time.split(":");
  let hours24 = parseInt(hours, 10);

  if (modifier === "PM" && hours24 !== 12) {
    hours24 += 12;
  }
  if (modifier === "AM" && hours24 === 12) {
    hours24 = 0;
  }

  return `${hours24.toString().padStart(2, "0")}:${minutes}`;
}
